import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from './components/AdminNavbar';
import { authAPI, academicYearsAPI } from '../../services/api';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const currentUser = authAPI.getCurrentUser();
    if (!currentUser) {
      navigate('/admin/login');
      return;
    }
    
    setUser(currentUser);
    fetchAcademicYears();
  }, [navigate]);

  const fetchAcademicYears = async () => {
  try {
    setLoading(true);
    setError('');
    
    const result = await academicYearsAPI.getAll();
    console.log('Dashboard fetch result:', result);
    
    // Check success flag
    if (result && result.success === true) {
      setAcademicYears(result.data || []);
    } else {
      // Handle API error response
      const errorMessage = result?.message || 'Failed to fetch academic years';
      setError(errorMessage);
      setAcademicYears([]);
      
      // Optional: Show success but empty data
      if (result && result.success === false && result.data) {
        setAcademicYears(result.data); // Some APIs return data even on success=false
      }
    }
  } catch (error) {
    console.error('Fetch error:', error);
    setError(error.message || 'Network error. Please check backend connection.');
    setAcademicYears([]);
  } finally {
    setLoading(false);
  }
};

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  if (!user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminNavbar onLogout={handleLogout} user={user} />
      
      <Container fluid className="py-4 admin-content">
        {/* Welcome Section */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="fw-bold mb-0">Admin Dashboard</h2>
            <p className="text-muted mb-0">
              Welcome back, <strong>{user.username}</strong>
            </p>
            <small className="text-muted">Role: {user.role || 'Admin'}</small>
          </Col>
          <Col className="text-end">
            <Button 
              variant="outline-primary" 
              onClick={() => console.log('User info:', user)}
            >
              <i className="fas fa-info-circle me-2"></i>
              Debug Info
            </Button>
          </Col>
        </Row>

        {/* Messages */}
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-calendar-alt fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">{academicYears.length}</h3>
                    <p className="mb-0">Academic Years</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={3} md={6} className="mb-3">
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-users fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">0</h3>
                    <p className="mb-0">Staff Members</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={3} md={6} className="mb-3">
            <Card className="bg-warning text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-book fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">0</h3>
                    <p className="mb-0">Courses</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={3} md={6} className="mb-3">
            <Card className="bg-info text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-building fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">0</h3>
                    <p className="mb-0">Departments</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Academic Years Table */}
        <Card className="shadow">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="fas fa-calendar-alt me-2 text-primary"></i>
              Academic Years
            </h4>
            <Button variant="primary" onClick={() => navigate('/admin/academic-years')}>
              <i className="fas fa-plus me-2"></i>
              Manage
            </Button>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading academic years...</p>
              </div>
            ) : academicYears.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Academic Year</th>
                      <th>Status</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {academicYears.map((year) => (
                      <tr key={year.id}>
                        <td>{year.id}</td>
                        <td>{year.academic_year}</td>
                        <td>
                          <span className={`badge bg-${year.status == 1 ? 'success' : 'danger'}`}>
                            {year.status == 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(year.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                <h5>No academic years found</h5>
                <p className="text-muted">Add your first academic year</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Quick Actions */}
        <Row className="mt-4">
          <Col lg={6}>
            <Card className="shadow">
              <Card.Header className="bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-bolt me-2 text-warning"></i>
                  Quick Actions
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button variant="outline-primary" onClick={() => navigate('/admin/academic-years')}>
                    <i className="fas fa-calendar-alt me-2"></i>
                    Manage Academic Years
                  </Button>
                  <Button variant="outline-success" onClick={() => navigate('/admin/staff')}>
                    <i className="fas fa-user-tie me-2"></i>
                    Manage Staff
                  </Button>
                  <Button variant="outline-warning" onClick={() => navigate('/admin/departments')}>
                    <i className="fas fa-sitemap me-2"></i>
                    Manage Departments
                  </Button>
                  <Button variant="outline-info" onClick={() => navigate('/admin/courses')}>
                    <i className="fas fa-book me-2"></i>
                    Manage Courses
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={6}>
            <Card className="shadow">
              <Card.Header className="bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-info-circle me-2 text-info"></i>
                  System Information
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="system-info">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Logged in as:</span>
                    <strong>{user.username}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>User ID:</span>
                    <code>{user.id}</code>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Token:</span>
                    <small className="text-truncate" style={{ maxWidth: '200px' }}>
                      {localStorage.getItem('admin_token')?.substring(0, 20)}...
                    </small>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Session:</span>
                    <span className="text-success">Active</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminDashboard;